import { debugLog, debugWarn } from '@/utils/debugLogger';

interface FavoritesCacheEntry {
  data: string[];
  timestamp: number;
  userId: string;
}

class FavoritesCache {
  private cache = new Map<string, FavoritesCacheEntry>();
  private readonly TTL = 5 * 60 * 1000; // 5 minutes
  private pendingRequests = new Map<string, Promise<string[]>>();

  /**
   * Get cached favorites for a user
   */
  get(userId: string): string[] | null {
    if (!userId) return null;
    
    const entry = this.cache.get(userId);
    if (!entry) return null;
    
    const isExpired = Date.now() - entry.timestamp > this.TTL;
    if (isExpired) {
      this.cache.delete(userId);
      debugLog('cache', 'Favorites cache expired for user', { userId }, 'FavoritesCache');
      return null;
    }
    
    debugLog('cache', 'Favorites cache hit', { userId, count: entry.data.length }, 'FavoritesCache');
    return entry.data;
  }

  /**
   * Set cached favorites for a user
   */
  set(userId: string, favorites: string[]): void {
    if (!userId) return;
    
    this.cache.set(userId, {
      data: favorites,
      timestamp: Date.now(),
      userId
    });
    
    debugLog('cache', 'Favorites cached', { userId, count: favorites.length }, 'FavoritesCache');
  }

  /**
   * Invalidate cache for a user
   */
  invalidate(userId: string): void {
    if (!userId) return;
    
    this.cache.delete(userId);
    this.pendingRequests.delete(userId);
    debugLog('cache', 'Favorites cache invalidated', { userId }, 'FavoritesCache');
  }

  /**
   * Deduplicate requests for the same user
   */
  async deduplicate<T>(
    userId: string, 
    requestFn: () => Promise<T>
  ): Promise<T> {
    const existing = this.pendingRequests.get(userId);
    if (existing) {
      debugLog('cache', 'Reusing pending favorites request', { userId }, 'FavoritesCache');
      return existing as Promise<T>;
    }

    debugLog('cache', 'Creating new favorites request', { userId }, 'FavoritesCache');
    const promise = requestFn();
    this.pendingRequests.set(userId, promise as Promise<string[]>);

    try {
      const result = await promise;
      return result;
    } finally {
      this.pendingRequests.delete(userId);
    }
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.cache.clear();
    this.pendingRequests.clear();
    debugLog('cache', 'All favorites cache cleared', undefined, 'FavoritesCache');
  }

  /**
   * Get cache stats
   */
  getStats() {
    return {
      cacheSize: this.cache.size,
      pendingRequests: this.pendingRequests.size,
      entries: Array.from(this.cache.entries()).map(([userId, entry]) => ({
        userId,
        count: entry.data.length,
        age: Date.now() - entry.timestamp
      }))
    };
  }
}

export const favoritesCache = new FavoritesCache();